import { BreakpointObserver } from '@angular/cdk/layout';
import { computed, inject, signal, Service } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { CONSTANTS } from '@shared/constants';

/**
 * Below this the shell has no room for a 96px rail beside the page, so the rail becomes something
 * you summon: off-canvas by default, over the page when opened. Kept in step with the same value
 * in the shell's own CSS.
 */
const COMPACT_QUERY = `(max-width: ${CONSTANTS.RESPONSIVENESS.SIDEBAR_BREAKPOINT - 0.02}px)`;

/**
 * Shell chrome state: one media query for the rail, the header and the launcher to agree on, plus
 * whether the compact rail is currently showing. Root-provided because the launcher, the rail and
 * the header all touch it and none of them should own the others.
 */
@Service()
export class ShellNavService {
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly requested = signal(false);

  constructor() {
    // A rail that outlived the navigation it started would cover the page it just opened. Each
    // top-level area has a shell of its own, and a navigation into another area ends before the
    // new shell is listening, so only this service sees every navigation end.
    inject(Router)
      .events.pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.closeRail());
  }

  readonly isCompact = toSignal(
    this.breakpointObserver.observe(COMPACT_QUERY).pipe(map(state => state.matches)),
    { initialValue: false }
  );

  /**
   * Whether the rail is showing as a temporary overlay. Only meaningful on a compact viewport —
   * gated on it so growing the window back to a permanent rail dismisses the overlay for free.
   */
  readonly isRailOpen = computed(() => this.requested() && this.isCompact());

  /**
   * A page whose bottom edge is its own primary control (Copilot's ask-bar) draws the rail's
   * launcher itself, where it does not cover that control, and the shell stands its own down.
   */
  readonly launcherInPage = signal(false);

  openRail(): void {
    this.requested.set(true);
  }

  closeRail(): void {
    this.requested.set(false);
  }

  toggleRail(): void {
    this.requested.update(open => !open);
  }
}
